/**
 * API clients for Timely Integration Cloud using ts-rest & react-query.
 */

import * as Sentry from '@sentry/browser';
import { apiSpec, type SyncTimelyToTicResponse } from '@timelyapp/integration-cloud-api-spec';
import {
  getJwtCookie,
  hasValidClientSessionSignalCookie,
  setClientSessionSignalCookie,
} from '@timelyapp/integration-cloud-sdk/utils/cookies';
import type { ApiFetcherArgs, ClientArgs } from '@ts-rest/core';
import { encodeQueryParams, initClient, tsRestFetchApi } from '@ts-rest/core';
import { initQueryClient } from '@ts-rest/react-query';
import {
  getIsLiveTimelyClientSideEnv,
  getTicApiBaseUrl,
  getTimelySessionUrlForTicJwtExchange,
} from './api.environment.utils';

export {
  getTicApiBaseUrl,
  getTicAuthExpirationMinutes,
  getTimelyApiBaseUrl,
  getTimelySessionUrlForTicJwtExchange,
} from './api.environment.utils';

/**
 * Custom query param serializer for TIC.
 * @link https://github.com/ts-rest/ts-rest/blob/main/libs/ts-rest/core/src/lib/query.ts#L41C1-L94C1
 */
export const ticQueryParamSerializer = encodeQueryParams;

export const getTicApiHeaders = (): Record<string, string> => {
  let headers: Record<string, string> = {};
  /**
   * Development only: The hand-rolled JWT's are stored in a cookie.
   * @note Supabase Auth sessions are separate (so don't try to get too cute with this)
   */

  const jwt = getJwtCookie();
  if (jwt) {
    headers = { ...headers, 'X-TIC-JWT': jwt };
  }

  // Pusher socket ID is used to exclude the sender from receiving their own events.
  const pusherSocketId =
    (typeof window !== 'undefined' && (window as any)?.TimelyPusherSocketId) || null;
  if (pusherSocketId) {
    headers = { ...headers, 'TL-Socket-Id': pusherSocketId };
  }
  /*
  This doesn't appear to have an effect in local dev + Spotlight, so skipping for now.
  Even when this was enabled, we ended up with (missing root transaction) in Sentry Spotlight?

  // Attach sentry
  const activeSpan = Sentry.getActiveSpan();
  const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined;

  // Include Sentry trace and baggage headers in API requests
  const sentryTraceHeader = rootSpan ? Sentry.spanToTraceHeader(rootSpan) : undefined;
  const sentryBaggageHeader = rootSpan ? Sentry.spanToBaggageHeader(rootSpan) : undefined;

  if (sentryTraceHeader && sentryBaggageHeader) {
    headers = {
      ...headers,
      baggage: sentryBaggageHeader,
      'sentry-trace': sentryTraceHeader,
    };
  }
  */

  return headers;
};

export const getTicApiCredentialsSetting = (): RequestCredentials => {
  if (getIsLiveTimelyClientSideEnv()) return 'include';
  return 'same-origin';
};

/**
 * Shared configuration for the API clients.
 * @link https://ts-rest.com/docs/core/custom
 */
const clientOptions = {
  baseUrl: getTicApiBaseUrl(),
  baseHeaders: {},
  api: async (args: ApiFetcherArgs & { debug?: boolean }) => {
    args.headers = { ...args.headers, ...getTicApiHeaders() };

    /**
     * In Timely+TIC production mode, include auth/cookies in requests.
     * Ref https://github.com/Timely/Frontend/pull/4068
     * @note See `api-route-with-cors.ts` setup for domain allow-list.
     */
    args.fetchOptions = {
      ...args.fetchOptions,
      credentials: getTicApiCredentialsSetting(),
    };

    /**
     * Special handling of TIC API authentication in Timely FE environment.
     *
     * In case client-side cookie doesn't exist to signal we've got a valid JWT-based session,
     * we need to refresh authentication by hitting the `/tic/sessions` endpoint, which will
     * set the httpOnly cookie (in prod). To register this new session for client-side (outside of redux),
     * `setClientSessionSignalCookie` is used.
     *
     * @warning This does not carry over to Redux/RTKQuery client's understanding of things,
     *          but that's well handeled via `TicContextProvider`, so shouldn't matter.
     */
    const isTimelyEnv = !!process.env.TIMELY_REACT_CLIENT;
    if (isTimelyEnv && !hasValidClientSessionSignalCookie()) {
      const url = getTimelySessionUrlForTicJwtExchange();

      try {
        const { jwtValidInMinutes } = await fetch(url, {
          ...args.fetchOptions,
          headers: args.headers,
        }).then(res => {
          if (!res.ok) throw new Error(`Failed to refresh TIC session via ${url}`);
          return res.json() as Promise<SyncTimelyToTicResponse>;
        });
        setClientSessionSignalCookie(jwtValidInMinutes);
      } catch (e) {
        Sentry.captureException(e);
        throw e;
      }
    }

    if (args.debug) console.log('API call', args);

    return tsRestFetchApi(args);
  },
} satisfies ClientArgs;

/**
 * Client to make API calls to Timely Integration Cloud.
 * @link https://ts-rest.com/docs/core/fetch
 */
export const fetchClient = initClient(apiSpec, clientOptions);

/**
 * React Query client to make API calls to Timely Integration Cloud.
 * @link https://ts-rest.com/docs/react-query
 */
export const queryClient = initQueryClient(apiSpec, clientOptions);
